import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { FEATURE_LIMITS } from '../utils/membership';
import { database } from '../supabase';

function membershipTierFromSupabaseRow(row) {
  if (!row) return 'free';
  const status = String(row.subscription_status || '').toLowerCase();
  const active = status === 'active' || status === 'trialing';
  if (!active) return 'free';
  const plan = String(row.subscription_plan || 'free').toLowerCase();
  if (plan === 'enterprise') return 'enterprise';
  if (plan === 'pro') return 'pro';
  return 'free';
}

function mergeLimitsForTier(tier) {
  const base = FEATURE_LIMITS.free;
  const tierLimits = FEATURE_LIMITS[tier] || base;
  return {
    ...base,
    ...tierLimits,
    youtubeVideos: tierLimits.youtubeVideos ?? base.youtubeVideos
  };
}

function buildDailyRemaining(limits, savedData, tier, today) {
  const sameDay =
    savedData &&
    savedData.lastReset === today &&
    savedData.membershipTier === tier;
  const cap = (limit, key) => {
    if (limit === 'unlimited') return 999999;
    const n = typeof limit === 'number' ? limit : 0;
    if (sameDay && typeof savedData.dailyRemaining?.[key] === 'number') {
      return Math.min(n, savedData.dailyRemaining[key]);
    }
    return n;
  };
  return {
    documentUploads: cap(limits.documentUploads, 'documentUploads'),
    aiResponses: cap(limits.aiResponses, 'aiResponses'),
    youtubeVideos: cap(limits.youtubeVideos, 'youtubeVideos'),
    quizGenerations:
      limits.aiResponses === 'unlimited'
        ? 999999
        : cap(limits.aiResponses, 'quizGenerations'),
    flashcardGenerations:
      limits.aiResponses === 'unlimited'
        ? 999999
        : cap(limits.aiResponses, 'flashcardGenerations')
  };
}

export function useMembership() {
  const { user } = useContext(AuthContext);
  const [membershipData, setMembershipData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load tier from Supabase (Stripe webhook → subscription_*) + local usage from localStorage
  useEffect(() => {
    if (!user) {
      setMembershipData(null);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);

      const today = new Date().toDateString();
      const storageKey = `membership_${user.uid}`;

      let savedData = null;
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          savedData = JSON.parse(raw);
          if (savedData.lastReset !== today) savedData = null;
        }
      } catch (e) {
        console.error('Error loading membership from localStorage:', e);
        savedData = null;
      }

      let tier = 'free';
      try {
        const row = await database.getUserByFirebaseUid(user.uid);
        tier = membershipTierFromSupabaseRow(row);
      } catch (e) {
        console.warn('Could not load Supabase user for membership tier:', e);
      }

      if (cancelled) return;

      const limits = mergeLimitsForTier(tier);
      const dailyUsage = savedData?.dailyUsage || {
        documentUploads: 0,
        aiResponses: 0,
        youtubeVideos: 0,
        quizGenerations: 0,
        flashcardGenerations: 0
      };
      const dailyRemaining = buildDailyRemaining(limits, savedData, tier, today);

      const emptyPercent = {
        documentUploads: 0,
        aiResponses: 0,
        youtubeVideos: 0,
        quizGenerations: 0,
        flashcardGenerations: 0
      };

      const initialData = {
        user: { uid: user.uid, email: user.email },
        membershipTier: tier,
        limits,
        lastReset: today,
        dailyUsage,
        dailyRemaining,
        dailyPercentage: emptyPercent,
        monthlyPercentage: emptyPercent
      };

      setMembershipData(initialData);
      try {
        localStorage.setItem(storageKey, JSON.stringify(initialData));
      } catch (e) {
        console.error('Error saving membership to localStorage:', e);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Load membership data - DISABLED to prevent timeouts
  const loadMembershipData = async () => {
    // Completely disable Firebase loading to prevent timeouts
    console.log('Membership loading disabled to prevent timeouts');
    return;
  };

  // Check if user can perform an action
  const canPerformAction = async (feature, isMonthly = false) => {
    if (!membershipData) {
      console.log('No membership data, allowing action');
      return true; // Allow if no data
    }
    
    const limit = membershipData.limits[feature];
    console.log(`Checking ${feature}: limit=${limit}, remaining=${membershipData.dailyRemaining[feature]}`);
    
    if (limit === 'unlimited') return true;
    
    const remaining = membershipData.dailyRemaining[feature];
    const canPerform = remaining > 0;
    console.log(`Can perform ${feature}: ${canPerform}`);
    return canPerform;
  };

  // Increment usage for an action
  const trackUsage = async (feature, isMonthly = false) => {
    if (!user || !membershipData) return false;
    
    console.log(`Tracking usage for ${feature}`);
    
    // Update the membership data state
    setMembershipData(prev => {
      if (!prev) return prev;
      
      const newUsage = { ...prev.dailyUsage };
      const newRemaining = { ...prev.dailyRemaining };
      
      // Increment usage
      newUsage[feature] = (newUsage[feature] || 0) + 1;
      
      // Decrement remaining
      const limit = prev.limits[feature];
      if (limit !== 'unlimited') {
        newRemaining[feature] = Math.max(0, (newRemaining[feature] || limit) - 1);
      }
      
      console.log(`Updated ${feature}: usage=${newUsage[feature]}, remaining=${newRemaining[feature]}`);
      
      const updatedData = {
        ...prev,
        dailyUsage: newUsage,
        dailyRemaining: newRemaining
      };
      
      // Save to localStorage
      try {
        const storageKey = `membership_${user.uid}`;
        localStorage.setItem(storageKey, JSON.stringify(updatedData));
        console.log('Updated membership data saved to localStorage');
      } catch (error) {
        console.error('Error saving updated membership data to localStorage:', error);
      }
      
      return updatedData;
    });
    
    return true;
  };

  // Get remaining usage for a feature
  const getRemaining = async (feature, isMonthly = false) => {
    if (!membershipData) return 0;
    
    return membershipData.dailyRemaining[feature] || 0;
  };

  // Get usage percentage for a feature
  const getUsagePercent = async (feature, isMonthly = false) => {
    if (!membershipData) return 0;
    
    const percentageKey = isMonthly ? 'monthlyPercentage' : 'dailyPercentage';
    return membershipData[percentageKey][feature] || 0;
  };

  // Computed values
  const currentTier = membershipData?.membershipTier || 'free';
  const isFree = currentTier === 'free';
  const isPro = currentTier === 'pro';
  const isEnterprise = currentTier === 'enterprise';

  return {
    membershipData,
    loading,
    error,
    loadMembershipData,
    canPerformAction,
    trackUsage,
    getRemaining,
    getUsagePercent,
    currentTier,
    isFree,
    isPro,
    isEnterprise
  };
}